/* Where Cardea is, what we ask it for, and where it sends the browser back.

   Cardea is Pocket ID v2.12.0 at https://cardea.rayray.cloud. Its endpoints are
   *pinned* here rather than fetched from /.well-known/openid-configuration at
   runtime. Two reasons, and they are worth stating because "just use discovery"
   is the reflex:

   - Discovery would add a cross-origin round-trip in front of every sign-in,
     on a document whose CORS headers we do not control and have no reason to
     trust more than the paths themselves.
   - Pocket ID's paths are not the conventional ones (`/authorize` at the root,
     but `/api/oidc/token`), so a hand-written guess would be wrong. These were
     read from the live discovery document on 2026-08-04 and are recorded below
     verbatim. If Cardea ever moves them, this file is the one place to change.

   Everything is derived from the issuer, so pointing VITE_CARDEA_ISSUER at a
   different Pocket ID instance works without editing code. */

/** Cardea in production. Overridable for a local or staging Pocket ID. */
const DEFAULT_ISSUER = 'https://cardea.rayray.cloud'

/* The RFC 8707 resource we ask the token to be audienced for.

   This is Milo's **HTTP API** resource, and it is deliberately NOT the MCP
   resource `https://milo.rayray.cloud/mcp`. They are two separately-registered
   Cardea API resources with two separate sets of permission ids:

     https://milo.rayray.cloud       ← this app. /history, /log, /nutrition, …
     https://milo.rayray.cloud/mcp   ← the claude.ai connectors. POST /mcp only.

   The rule behind the split: a client that calls anything other than /mcp is not
   an MCP client and gets its own resource. This SPA never touches /mcp, so a
   token minted for it has no business being accepted there — and the backend
   enforces exactly that, validating the REST routes against
   MILO_API_RESOURCE_URL and /mcp against MILO_MCP_RESOURCE_URL.

   The scope *strings* are `milo:read`/`milo:write` on both resources; only the
   permission ids differ. Pocket ID allows a key to repeat across APIs, so the
   two are told apart by audience alone — which is why sending the right
   `resource` here is load-bearing rather than cosmetic.

   `resource` is IMMUTABLE in Pocket ID once created. Changing this string means
   registering a new resource, not editing one. */
const DEFAULT_RESOURCE = 'https://milo.rayray.cloud'

/* What we ask for.

   - `openid` gets us an ID token, which is the only thing that carries a human
     name to put in the header.
   - `profile` is what puts a name *in* that ID token. `openid` alone yields
     `sub` and nothing else, and `sub` is a UUID — which is exactly what the
     header used to render (#55). With `profile`, Pocket ID emits
     `preferred_username`, `name`, `display_name`, `given_name`, `family_name`
     and `picture`, and the existing chain in jwt.ts resolves the first of them.
     **To revert, delete the word `profile` from the string below. That is the
     entire change** — nothing else in the app reads a profile claim, and
     jwt.ts already falls back to `sub` when none are present.
   - `offline_access` is what makes Cardea issue a refresh token. Without it a
     reload is a full trip through Cardea.
   - `milo:read` / `milo:write` are Milo's permissions on the HTTP API resource
     above — same key strings as the MCP resource's, different permission ids.

   `profile` cannot be refused. Pocket ID has no per-client scope allow-list:
   `Client.GetScopes()` returns openid/profile/email/groups/offline_access for
   every client unconditionally, and `resolveResource` seeds the grantable set
   with those same five before it looks at the RFC 8707 resource at all. So this
   cannot fail as `invalid_scope` the way an unregistered custom scope would.
   (Read from pocket-id v2.12.0, the version Cardea runs.)

   Expect the issued token's `aud` to be `[resource, issuer]`, not one element:
   Pocket ID appends the issuer when an identity scope is requested alongside an
   RFC 8707 `resource`. That is normal, and the backend tests `aud` by
   membership. **`profile` does not change this.** The issuer is appended on a
   boolean — "was any identity scope granted?" — which `openid` already answered
   yes, and the append is guarded against duplicating it. Adding a second
   identity scope adds no second audience. */
export const SCOPES = 'openid profile offline_access milo:read milo:write'

export interface AuthConfig {
  issuer: string
  authorizationEndpoint: string
  tokenEndpoint: string
  /** Advertised by Cardea, but deliberately not used — see session.ts signOut. */
  endSessionEndpoint: string
  clientId: string
  resource: string
  /** Always this origin's /callback, so dev and prod differ only by origin. */
  redirectUri: string
}

/** Thrown when the app is not configured well enough to start a sign-in. */
export class AuthConfigError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'AuthConfigError'
  }
}

function env(key: string): string | undefined {
  const value = (import.meta.env as Record<string, string | undefined>)[key]
  return value && value.trim() !== '' ? value.trim() : undefined
}

/**
 * Strip a trailing slash.
 *
 * On the issuer, so `${issuer}/authorize` never doubles it. On the resource,
 * because the backend compares `aud` to `MILO_API_RESOURCE_URL` byte for byte —
 * `VITE_MILO_RESOURCE=https://milo.rayray.cloud/` would mint tokens that every
 * REST route rejects as 401, with nothing local to explain why.
 */
function stripTrailingSlash(value: string): string {
  return value.replace(/\/+$/, '')
}

/* Read once and cache: the values come from build-time env, so they cannot
   change during a session, and re-reading would only re-throw the same error. */
let cached: AuthConfig | null = null

/**
 * The live auth configuration.
 *
 * Throws `AuthConfigError` when `VITE_CARDEA_CLIENT_ID` is unset. That is
 * deliberate and loud: a sign-in button that silently does nothing, or that
 * redirects to Cardea with `client_id=undefined` and returns an opaque
 * `invalid_client`, costs far more to diagnose than a legible message on the
 * sign-in page.
 */
export function authConfig(): AuthConfig {
  if (cached) return cached

  const clientId = env('VITE_CARDEA_CLIENT_ID')
  if (!clientId) {
    throw new AuthConfigError(
      'VITE_CARDEA_CLIENT_ID is not set, so there is no Cardea client to sign in ' +
        'to. Register a public OIDC client in Cardea and set the id in web/.env ' +
        '(see web/.env.example).',
    )
  }

  const issuer = stripTrailingSlash(env('VITE_CARDEA_ISSUER') ?? DEFAULT_ISSUER)

  cached = {
    issuer,
    authorizationEndpoint: `${issuer}/authorize`,
    tokenEndpoint: `${issuer}/api/oidc/token`,
    endSessionEndpoint: `${issuer}/api/oidc/end-session`,
    clientId,
    resource: stripTrailingSlash(env('VITE_MILO_RESOURCE') ?? DEFAULT_RESOURCE),
    redirectUri: `${window.location.origin}/callback`,
  }
  return cached
}

/** The path Cardea redirects back to. Referenced by App and by vite.config.ts's comment. */
export const CALLBACK_PATH = '/callback'
